import { useEffect, useMemo, useState } from 'react';
import type { WeekGridInputs } from '../assembly';
import type { Filters } from '../FilterBar';
import { fieldOf, type ObjectSummary } from '../format';
import { civil, mergeFilters } from '../list-state';
import { shortId, useReferenceLabels } from '../reference-labels';
import { useAppServices } from '../ViewContext';
import { useCellRenderer, type Column } from './cells';
import { useCollection, type Row } from './useCollection';

/**
 * Renderer du composant Core `WeekGrid` (ADR-125 §6, le semainier du v1) : une ligne par objet cité par
 * `row_field`, un jour par colonne ; la semaine visible devient un filtre `date_field gte / lt` sous la
 * même recherche et les mêmes filtres que la liste. Une case montre l'état (libellé toujours écrit, ton
 * du thème) et son nombre d'enregistrements ; un clic ouvre le dernier. Les lignes attendues (`rows`)
 * restent visibles sans enregistrement ; la rangée d'alertes s'allume sur `alert_values`. Le périmètre
 * (`rows.scope`, le site) restreint l'affichage, jamais l'autorisation ; un clic sur un jour ouvre son détail.
 */

const MAX_RECORDS = 2000;
const DAY_NAMES = ['lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.', 'dim.'];

function startOfWeek(date: Date): Date {
  const day = (date.getDay() + 6) % 7;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() - day);
}

/** Numéro de semaine ISO 8601. */
export function isoWeek(date: Date): number {
  const target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = target.getUTCDay() || 7;
  target.setUTCDate(target.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
  return Math.ceil(((target.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
}

function dayOf(value: unknown): string | null {
  if (typeof value !== 'string' || value === '') return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const instant = new Date(value);
  return Number.isNaN(instant.getTime()) ? null : civil(instant);
}

const TONE_CLASS: Record<string, string> = { ok: 'ok', warn: 'warn', danger: 'danger', info: 'info', neutral: 'neutral' };

export function WeekGridRenderer({
  type,
  object,
  grid,
  listColumns,
  baseFilter,
  search,
  refreshKey,
  selectedId,
  onOpen,
}: {
  type: string;
  object: ObjectSummary | undefined;
  grid: WeekGridInputs;
  listColumns: readonly Column[];
  baseFilter: Filters;
  search: { q?: string; q_fields?: readonly string[] };
  refreshKey: number;
  selectedId: string | null;
  onOpen: (row: Row) => void;
}) {
  const services = useAppServices();
  const [anchor, setAnchor] = useState(() => new Date());
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const [scope, setScope] = useState('');
  const [openDay, setOpenDay] = useState<string | null>(null);
  const from = useMemo(() => startOfWeek(anchor), [anchor]);
  const days = useMemo(() => Array.from({ length: 7 }, (_, index) => new Date(from.getFullYear(), from.getMonth(), from.getDate() + index)), [from]);
  const to = new Date(from.getFullYear(), from.getMonth(), from.getDate() + 7);
  const dateType = fieldOf(object, grid.date_field)?.type;
  const bound = (date: Date): string => (dateType === 'date' ? civil(date) : date.toISOString());
  const filter = mergeFilters({ [grid.date_field]: { gte: bound(from), lt: bound(to) } }, baseFilter);
  const collection = useCollection({ type, input: { filter, sort: [{ field: grid.date_field, direction: 'asc' }], ...(search.q === undefined ? {} : { q: search.q, ...(search.q_fields === undefined ? {} : { q_fields: search.q_fields }) }) }, pageSize: 200 }, refreshKey);
  useEffect(() => {
    if (collection.status === 'ready' && !collection.done && !collection.loadingMore && collection.moreError === null && collection.rows.length < MAX_RECORDS) collection.loadMore();
  }, [collection]);
  // Lignes attendues : les objets de la source, même sans enregistrement dans la semaine.
  const expected = useCollection(grid.rows === undefined ? null : { type: grid.rows.object_type, input: { ...(grid.rows.filter === undefined ? {} : { filter: grid.rows.filter as Filters }) }, pageSize: 200 }, refreshKey);
  const render = useCellRenderer(object, listColumns, [grid.state_field, ...(grid.sub_field === undefined ? [] : [grid.sub_field])], collection.rows, collection.meta);

  const rowType = fieldOf(object, grid.row_field)?.reference_type;
  const scopeSpec = grid.rows?.scope;
  const allExpected = grid.rows === undefined ? [] : expected.rows;
  const scopeIds = scopeSpec === undefined ? [] : [...new Set(allExpected.map((row) => row[scopeSpec.field]).filter((value): value is string => typeof value === 'string' && value !== ''))];
  const expectedRows = scopeSpec === undefined || scope === '' ? allExpected : allExpected.filter((row) => row[scopeSpec.field] === scope);
  // Un périmètre choisi : seules ses lignes, et les enregistrements de ces lignes.
  const inScope = scopeSpec === undefined || scope === '' ? null : new Set(expectedRows.map((row) => row.id).filter((id): id is string => typeof id === 'string'));
  const cited = new Set<string>();
  for (const row of collection.rows) if (typeof row[grid.row_field] === 'string' && (inScope === null || inScope.has(row[grid.row_field] as string))) cited.add(row[grid.row_field] as string);
  for (const row of expectedRows) if (typeof row.id === 'string') cited.add(row.id);
  const groupIds = grid.rows?.group_field === undefined ? [] : expectedRows.map((row) => row[grid.rows!.group_field!]).filter((value): value is string => typeof value === 'string' && /^[0-9a-f]{24}$/.test(value));
  const labels = useReferenceLabels(services.client, [
    ...(rowType === undefined ? [] : [{ type: rowType, ids: [...cited] }]),
    ...(grid.rows === undefined || groupIds.length === 0 ? [] : [{ type: grid.rows.object_type, ids: groupIds }]),
    ...(scopeSpec === undefined || scopeIds.length === 0 ? [] : [{ type: scopeSpec.object_type, ids: scopeIds }]),
  ]);
  const ownLabel = (id: string): string => {
    const source = expectedRows.find((row) => row.id === id);
    const field = grid.rows?.label_field;
    if (source !== undefined && field !== undefined && typeof source[field] === 'string') return source[field] as string;
    return labels.get(id) ?? shortId(id);
  };
  const groupOf = (id: string): string => {
    const field = grid.rows?.group_field;
    if (field === undefined) return '';
    const value = expectedRows.find((row) => row.id === id)?.[field];
    if (typeof value !== 'string' || value === '') return 'Autres';
    return /^[0-9a-f]{24}$/.test(value) ? (labels.get(value) ?? shortId(value)) : value;
  };

  const cells = new Map<string, Row[]>();
  for (const row of collection.rows) {
    const rowId = row[grid.row_field];
    const day = dayOf(row[grid.date_field]);
    if (typeof rowId !== 'string' || day === null || !cited.has(rowId)) continue;
    const key = `${rowId}|${day}`;
    cells.set(key, [...(cells.get(key) ?? []), row]);
  }
  const ordered = [...cited].sort((a, b) => groupOf(a).localeCompare(groupOf(b), 'fr') || ownLabel(a).localeCompare(ownLabel(b), 'fr'));
  const groups = new Map<string, string[]>();
  for (const id of ordered) groups.set(groupOf(id), [...(groups.get(groupOf(id)) ?? []), id]);
  const alerts = new Set(grid.alert_values ?? []);
  const today = civil(new Date());
  const stateOf = (rows: readonly Row[]): string => String(rows[rows.length - 1]?.[grid.state_field] ?? '');
  const toneOf = (value: string): string => TONE_CLASS[(grid.tones as Record<string, string> | undefined)?.[value] ?? 'neutral'] ?? 'neutral';
  const shift = (direction: -1 | 1): void => setAnchor((current) => new Date(current.getFullYear(), current.getMonth(), current.getDate() + 7 * direction));
  const toggle = (group: string): void => setCollapsed((current) => {
    const next = new Set(current);
    if (next.has(group)) next.delete(group);
    else next.add(group);
    return next;
  });
  const last = new Date(to.getFullYear(), to.getMonth(), to.getDate() - 1);
  const title = `Semaine ${isoWeek(from)} · ${from.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} – ${last.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}`;

  return (
    <div className="ay-weekgrid">
      <div className="ay-agenda__bar">
        <div className="ay-agenda__nav">
          <button type="button" className="ay-button ay-button--secondary" onClick={() => shift(-1)} aria-label="Semaine précédente">
            ‹
          </button>
          <button type="button" className="ay-button ay-button--secondary" onClick={() => setAnchor(new Date())}>
            Cette semaine
          </button>
          <button type="button" className="ay-button ay-button--secondary" onClick={() => shift(1)} aria-label="Semaine suivante">
            ›
          </button>
        </div>
        <h2 className="ay-agenda__title" aria-live="polite">
          {title}
        </h2>
        {scopeSpec !== undefined && scopeIds.length > 0 && (
          <label className="ay-weekgrid__scope">
            <span>{scopeSpec.label?.['fr'] ?? 'Périmètre'}</span>
            <select className="ay-input" value={scope} onChange={(event) => setScope(event.target.value)}>
              <option value="">Tous</option>
              {[...scopeIds].sort((a, b) => (labels.get(a) ?? a).localeCompare(labels.get(b) ?? b, 'fr')).map((id) => (
                <option key={id} value={id}>
                  {labels.get(id) ?? shortId(id)}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      {collection.status === 'error' ? (
        <div role="alert" className="ay-banner ay-banner--danger">
          {collection.error?.message}{' '}
          <button type="button" className="ay-button ay-button--secondary" onClick={collection.reload}>
            Réessayer
          </button>
        </div>
      ) : (
        <div className="ay-weekgrid__scroll" aria-busy={collection.status === 'loading' || collection.loadingMore}>
          {collection.rows.length >= MAX_RECORDS && !collection.done && <p className="ay-banner ay-banner--warning" role="status">Plus de {MAX_RECORDS.toLocaleString('fr')} enregistrements sur la semaine : restreindre les filtres.</p>}
          <table className="ay-weekgrid__table">
            <thead>
              <tr>
                <th scope="col" className="ay-weekgrid__corner">{grid.row_header?.['fr'] ?? 'Ligne / jour'}</th>
                {days.map((day, index) => (
                  <th key={civil(day)} scope="col" className={`ay-weekgrid__day${civil(day) === today ? ' ay-weekgrid__day--today' : ''}${civil(day) === openDay ? ' ay-weekgrid__day--open' : ''}`}>
                    <button type="button" aria-expanded={civil(day) === openDay} aria-label={`Détail du ${day.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}`} onClick={() => setOpenDay((current) => (current === civil(day) ? null : civil(day)))}>
                      <span>{DAY_NAMES[index]}</span>
                      <span className="ay-weekgrid__date">{day.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>
                    </button>
                  </th>
                ))}
              </tr>
              {alerts.size > 0 && (
                <tr>
                  <th scope="row" className="ay-weekgrid__rowhead">Alertes</th>
                  {days.map((day) => {
                    const key = civil(day);
                    const count = [...cells.entries()].filter(([cell, rows]) => cell.endsWith(`|${key}`) && rows.some((row) => alerts.has(String(row[grid.state_field] ?? '')))).length;
                    return (
                      <td key={key} className={`ay-weekgrid__alert ay-weekgrid__alert--${count > 0 ? 'danger' : 'none'}`}>
                        {count > 0 ? `${count} ${count > 1 ? 'alertes' : 'alerte'}` : '—'}
                      </td>
                    );
                  })}
                </tr>
              )}
            </thead>
            {[...groups.entries()].map(([group, ids]) => (
              <tbody key={group || 'all'}>
                {group !== '' && (
                  <tr>
                    <th colSpan={8} scope="colgroup" className="ay-weekgrid__group">
                      <button type="button" aria-expanded={!collapsed.has(group)} onClick={() => toggle(group)}>
                        <span aria-hidden="true">{collapsed.has(group) ? '▸' : '▾'}</span> {group} <span className="ay-weekgrid__count">{ids.length}</span>
                      </button>
                    </th>
                  </tr>
                )}
                {!collapsed.has(group) &&
                  ids.map((id) => (
                    <tr key={id}>
                      <th scope="row" className="ay-weekgrid__rowhead">{ownLabel(id)}</th>
                      {days.map((day) => {
                        const key = civil(day);
                        const rows = cells.get(`${id}|${key}`) ?? [];
                        if (rows.length === 0) return <td key={key} className="ay-weekgrid__cell ay-weekgrid__cell--empty" aria-label={`${ownLabel(id)}, ${key} : rien d’enregistré`} />;
                        const lastRow = rows[rows.length - 1]!;
                        const state = stateOf(rows);
                        const rowKey = typeof lastRow.id === 'string' ? lastRow.id : `${id}-${key}`;
                        return (
                          <td key={key} className="ay-weekgrid__cell">
                            <button type="button" data-opens-record className={`ay-weekgrid__entry ay-weekgrid__entry--${toneOf(state)}${selectedId === rowKey ? ' ay-weekgrid__entry--selected' : ''}`} onClick={() => onOpen(lastRow)}>
                              <strong>{render.display(grid.state_field, lastRow)}</strong>
                              {grid.sub_field !== undefined && <span className="ay-weekgrid__sub">{render.display(grid.sub_field, lastRow)}</span>}
                              {rows.length > 1 && <span className="ay-weekgrid__sub">{rows.length} enregistrements</span>}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
              </tbody>
            ))}
          </table>
          {ordered.length === 0 && collection.status === 'ready' && <p className="ay-weekgrid__empty">Rien d’enregistré cette semaine.</p>}
        </div>
      )}
      {openDay !== null && (
        <DayDetail
          day={openDay}
          ids={ordered}
          cells={cells}
          labelOf={ownLabel}
          stateField={grid.state_field}
          subField={grid.sub_field}
          display={render.display}
          toneOf={toneOf}
          dateIsInstant={dateType !== 'date'}
          dateField={grid.date_field}
          onOpen={onOpen}
          onClose={() => setOpenDay(null)}
        />
      )}
    </div>
  );
}

/** Détail d'un jour : chaque ligne qui a des enregistrements ce jour-là, tous ses enregistrements (pas seulement le dernier), puis les lignes attendues restées vides. */
function DayDetail({
  day,
  ids,
  cells,
  labelOf,
  stateField,
  subField,
  display,
  toneOf,
  dateIsInstant,
  dateField,
  onOpen,
  onClose,
}: {
  day: string;
  ids: readonly string[];
  cells: ReadonlyMap<string, Row[]>;
  labelOf: (id: string) => string;
  stateField: string;
  subField: string | undefined;
  display: (field: string, row: Row) => React.ReactNode;
  toneOf: (value: string) => string;
  dateIsInstant: boolean;
  dateField: string;
  onOpen: (row: Row) => void;
  onClose: () => void;
}) {
  const [year, month, date] = day.split('-').map(Number) as [number, number, number];
  const title = new Date(year, month - 1, date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const filled = ids.filter((id) => (cells.get(`${id}|${day}`) ?? []).length > 0);
  const empty = ids.filter((id) => (cells.get(`${id}|${day}`) ?? []).length === 0);
  const count = filled.reduce((total, id) => total + (cells.get(`${id}|${day}`) ?? []).length, 0);
  return (
    <section className="ay-weekgrid__detail" role="region" aria-label={`Détail du ${title}`}>
      <header className="ay-weekgrid__detail-head">
        <h3>
          {title} <span className="ay-weekgrid__count">{count} {count > 1 ? 'enregistrements' : 'enregistrement'}</span>
        </h3>
        <button type="button" className="ay-button ay-button--secondary" onClick={onClose}>
          Fermer
        </button>
      </header>
      {filled.length === 0 ? (
        <p className="ay-weekgrid__empty">Rien d’enregistré ce jour.</p>
      ) : (
        <ul className="ay-weekgrid__detail-list">
          {filled.map((id) => (
            <li key={id}>
              <span className="ay-weekgrid__detail-row">{labelOf(id)}</span>
              <span className="ay-weekgrid__detail-entries">
                {(cells.get(`${id}|${day}`) ?? []).map((row, index) => {
                  const at = row[dateField];
                  const time = dateIsInstant && typeof at === 'string' ? new Date(at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : null;
                  return (
                    <button key={typeof row.id === 'string' ? row.id : index} type="button" data-opens-record className={`ay-weekgrid__entry ay-weekgrid__entry--${toneOf(String(row[stateField] ?? ''))}`} onClick={() => onOpen(row)}>
                      <strong>{display(stateField, row)}</strong>
                      {time !== null && <span className="ay-weekgrid__sub">{time}</span>}
                      {subField !== undefined && <span className="ay-weekgrid__sub">{display(subField, row)}</span>}
                    </button>
                  );
                })}
              </span>
            </li>
          ))}
        </ul>
      )}
      {empty.length > 0 && (
        <p className="ay-weekgrid__detail-missing">
          <strong>Sans enregistrement ({empty.length}) :</strong> {empty.map(labelOf).join(', ')}
        </p>
      )}
    </section>
  );
}

